import qs from 'qs';
import { StatusCodes } from 'http-status-codes';

import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import MessagingService from 'services/MessagingService/MessagingService';
import {
  GET_PRIVATE_DIRECTORY_CAMS_ERROR,
  GET_PRIVATE_DIRECTORY_CAMS_RESPONSE,
  GET_PRIVATE_DIRECTORY_CAROUSEL_CAMS_ERROR,
  GET_PRIVATE_DIRECTORY_CAROUSEL_CAMS_RESPONSE,
  GET_PRIVATE_DIRECTORY_FILTERS_ERROR,
  GET_PRIVATE_DIRECTORY_FILTERS_RESPONSE,
  GET_PRIVATE_DIRECTORY_KINKS_ERROR,
  GET_PRIVATE_DIRECTORY_KINKS_RESPONSE,
  GET_PRIVATE_DIRECTORY_USER_PRIVATE_SHOW_SETTINGS,
  GET_PRIVATE_DIRECTORY_USER_PRIVATE_SHOW_SETTINGS_ERROR,
  PRIVATE_DIRECTORY_PRIVATE_SHOW_CANCEL,
  PRIVATE_DIRECTORY_PRIVATE_SHOW_SENT,
} from 'services/MessagingService/EventNames';

import {
  CAMS_PER_PAGE,
  DEFAULT_ACTIVE_PAGE,
  GENDER,
  PS_ACTIONS,
  SORT_OPTION,
} from './config';


const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);
const WEBCHAT_HOST = ConfigurationService.get(ConfigurationKeys.WEB_CHAT_HOST);

const API_URL = `${BASE_URL}/rest/v1.0/premiumPrivateShows`;
const API_KINKS_URL = `${BASE_URL}/rest/v1.0/directoryTags`;
const NEW_API_KINKS_URL = `${BASE_URL}/rest/v2.0/directory/tags`;
const NEW_API_FILTERS_URL = `${BASE_URL}/rest/v2.0/directory/filters`;
const WEBCHAT_API = `${WEBCHAT_HOST}privateShowAction`;

class PrivateDirectoryService {
  constructor(messagingService) {
    this.messagingService = messagingService();
  }

  /* eslint-disable max-len */
  /**
   * @method getCams - get cams for ppd
   * {@link https://cam4com.atlassian.net/wiki/spaces/PROJ/pages/1772093640/Documentation+Private+Directory+API+-+Phase+1 documentation}
   * @param {string} gender - gender
   * @param {Object} rest - rest of the params
   */
  /* eslint-enable max-len */
  getCams = ({ gender, ...rest }) => {
    const query = {
      privateStreamer: true,
      resultsPerPage: CAMS_PER_PAGE,
      ...rest,
      ...this._getGenderQuery(gender),
    };
    const URL = encodeURI(`${API_URL}${qs.stringify(query, { addQueryPrefix: true, arrayFormat: 'repeat' })}`);
    ApiService.get(URL)
      .then((response) => {
        this.messagingService.emit(GET_PRIVATE_DIRECTORY_CAMS_RESPONSE, response.data);
      })
      .catch((error) => {
        this.messagingService.emit(GET_PRIVATE_DIRECTORY_CAMS_ERROR, error);
      });
  };

  /* eslint-disable max-len */
  /**
   * @method getSliderCams - get cams for the carausel / slider
   * {@link https://cam4com.atlassian.net/wiki/spaces/PROJ/pages/1772093640/Documentation+Private+Directory+API+-+Phase+1 documentation}
   * @param {string} genderPreference - gender
   */
  /* eslint-enable max-len */
  getSliderCams = (genderPreference) => {
    const sliderQuery = {
      directoryJson: true,
      online: true,
      url: true,
      page: DEFAULT_ACTIVE_PAGE,
      orderBy: SORT_OPTION.PS_EARNINGS,
      resultsPerPage: 9,
    };

    const query = {
      ...sliderQuery,
      ...this._getGenderQuery(genderPreference),
    };

    const URL = encodeURI(`${API_URL}${qs.stringify(query, { addQueryPrefix: true, arrayFormat: 'repeat' })}`);
    ApiService.get(URL)
      .then((response) => {
        if (response.status === StatusCodes.OK) {
          this.messagingService.emit(GET_PRIVATE_DIRECTORY_CAROUSEL_CAMS_RESPONSE, response.data);
        }
      })
      .catch((error) => {
        this.messagingService.emit(GET_PRIVATE_DIRECTORY_CAROUSEL_CAMS_ERROR, error);
      });
  };

  /* eslint-disable max-len */
  /**
   * @method getFilters - get filters for PPD
   * {@link https://cam4com.atlassian.net/wiki/spaces/PROJ/pages/1772093640/Documentation+Private+Directory+API+-+Phase+1 documentation}
   */
  /* eslint-enable max-len */
  getFilters = () => {
    ApiService.get(encodeURI(NEW_API_FILTERS_URL))
      .then((response) => {
        this.messagingService.emit(GET_PRIVATE_DIRECTORY_FILTERS_RESPONSE, response.data);
      })
      .catch((error) => {
        this.messagingService.emit(GET_PRIVATE_DIRECTORY_FILTERS_ERROR, error);
      });
  };

  /* eslint-disable max-len */
  /**
   * @method getKinks - get kinks for PPD
   * {@link https://cam4com.atlassian.net/wiki/spaces/PROJ/pages/1772093640/Documentation+Private+Directory+API+-+Phase+1 documentation}
   * @param {string} genderPreference - gender
   * @param {Object} rest - filterQuery, tokensQuery
   */
  /* eslint-enable max-len */
  getKinks = ({ genderPreference, ...rest }) => {
    const query = {
      privateStreamer: true,
      online: true,
      orderBy: SORT_OPTION.VIDEO_QUALITY,
      ...rest,
      ...this._getGenderQuery(genderPreference),
    };
    let tempUrl = API_KINKS_URL;
    tempUrl = NEW_API_KINKS_URL;
    query.usersPerTag = 0;
    query.page = 1;
    query.resultsPerPage = 90;
    delete query.orderBy;
    delete query.online;

    const URL = encodeURI(`${tempUrl}${qs.stringify(query, { addQueryPrefix: true, arrayFormat: 'repeat' })}`);
    ApiService.get(URL)
      .then((response) => {
        this.messagingService.emit(GET_PRIVATE_DIRECTORY_KINKS_RESPONSE, response.data.showTags);
      })
      .catch((error) => {
        this.messagingService.emit(GET_PRIVATE_DIRECTORY_KINKS_ERROR, error);
      });
  };

  /**
   * @method _getGenderQuery - internal function for returning object that contains gender and broadcast type
   * @param {string} genderPreference - gender
   */
  _getGenderQuery = (genderPreference) => {
    const soloAndGroup = [GENDER.SOLO, GENDER.MALE_FEMALE_GROUP];
    switch (genderPreference) {
      case GENDER.MALE: {
        return {
          gender: GENDER.MALE,
          broadcastType: [...soloAndGroup, GENDER.MALE_GROUP],
        };
      }
      case GENDER.SHEMALE: {
        return { gender: GENDER.SHEMALE };
      }
      case GENDER.COUPLE: {
        return { broadcastType: [GENDER.MALE_GROUP, GENDER.FEMALE_GROUP, GENDER.MALE_FEMALE_GROUP] };
      }
      case GENDER.ALL: {
        //  don't pass anything as broadcastType query params
        /*  privateStreamer: true
            resultsPerPage: 24
            page: 1
            orderBy: VIDEO_QUALITY
        */
        return {};
      }
      default: {
        return {
          gender: GENDER.FEMALE,
          broadcastType: [...soloAndGroup, GENDER.FEMALE_GROUP],
        };
      }
    }
  };

  /* eslint-disable max-len */
  /**
   * @method getPrivateShowAction - retriving the private show setting from the broadcaster
   * {@link https://cam4com.atlassian.net/wiki/spaces/RBN/pages/2545188916/Private+Directory+-+Private+Show+Offline+Requestsdocumentation}
   * @param {string} action - startPS/ request / cancelRequest
   * @param {string} roomname - broadcaster username
   * @param {string} username - username of the user that requests the PS
   * @param {string} accessHash - accessHash of the user that reqeusts the PS
   * @param {Object} rest
   */
  /* eslint-enable max-len */
  getPrivateShowAction = ({ action, roomname, username, accessHash, ...rest }) => {
    const query = {
      action,
      roomname,
      username,
      accessHash,
      ...rest,
    };
    const URL = encodeURI(`${WEBCHAT_API}${qs.stringify(query, { addQueryPrefix: true, arrayFormat: 'repeat' })}`);
    ApiService.get(URL)
      .then((response) => {
        if (action === PS_ACTIONS.CANCEL_REQUEST) {
          this.messagingService.emit(PRIVATE_DIRECTORY_PRIVATE_SHOW_CANCEL);
        } else {
          this.messagingService.emit(GET_PRIVATE_DIRECTORY_USER_PRIVATE_SHOW_SETTINGS, response.data.settings);
        }
      })
      .catch((error) => {
        this.messagingService.emit(GET_PRIVATE_DIRECTORY_USER_PRIVATE_SHOW_SETTINGS_ERROR, error);
      });
  };

  /* eslint-disable max-len */
  /**
   * @method privateShowAction - sending the offline request to the broadcaster
   * {@link https://cam4com.atlassian.net/wiki/spaces/RBN/pages/2545188916/Private+Directory+-+Private+Show+Offline+Requestsdocumentation}
   * @param {string} action - startPS/ request / cancelRequest
   * @param {string} roomname - broadcaster username
   * @param {string} username - username of the user that requests the PS
   * @param {string} accessHash - accessHash of the user that reqeusts the PS
   * @param {Object} rest
   */
  /* eslint-enable max-len */
  privateShowAction = ({ action, roomname, username, accessHash, ...rest }) => {
    const data = {
      action,
      roomname,
      ...rest,
    };
    const query = {
      username,
      accessHash,
    };
    const URL = encodeURI(`${WEBCHAT_API}${qs.stringify(query, { addQueryPrefix: true, arrayFormat: 'repeat' })}`);
    const config = {
      data: qs.stringify(data),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    };

    ApiService.post(URL, config)
      .then((response) => {
        this.messagingService.emit(PRIVATE_DIRECTORY_PRIVATE_SHOW_SENT, response.data);
      })
      .catch((error) => {
        this.messagingService.emit(GET_PRIVATE_DIRECTORY_USER_PRIVATE_SHOW_SETTINGS_ERROR, error);
      });
  };
}

export default new PrivateDirectoryService(MessagingService);
